// Docs: IMPLEMENTATION.md § "Headword template"; FEATURES.md § "Word headers".
//
// Tokenizer for grammar_info.headword, e.g.
//   "$JMD_DAT#x7q $PREP:MIT#k3f $ETW_DAT#k3f vom Halse bleiben"
// The backend (backend/app/headword.py) builds the template; this file only
// splits it and maps tokens to display text. It never parses German.

const TOKEN_RE = /\$(PREP:[^\s#$]+|[A-Z]+(?:_[A-Z]+)?)(?:#([a-z0-9]{3}))?/g;

const CODE_TO_CASE = {
  NOM: 'nominativ',
  AKK: 'akkusativ',
  DAT: 'dativ',
  GEN: 'genitiv',
  AKKDAT: 'akkusativ/dativ',
};

const JMD_ABBR = { NOM: 'jmd.', AKK: 'jmdn.', DAT: 'jmdm.', GEN: 'jmds.' };

/**
 * Describe one token name.
 * Returns { tokenType: 'slot'|'preposition'|'reflexive', caseValue, abbreviation, preposition }.
 */
export const describeToken = (name, id = null) => {
  if (name.startsWith('PREP:')) {
    return { tokenType: 'preposition', id, preposition: name.slice(5).toLowerCase() };
  }
  if (name.startsWith('SICH_')) {
    return { tokenType: 'reflexive', id, caseValue: CODE_TO_CASE[name.slice(5)] || '' };
  }
  const [head, code] = name.includes('_') ? name.split('_') : [null, name];
  if (head === 'JMD') {
    return { tokenType: 'slot', id, kind: 'JMD', caseValue: CODE_TO_CASE[code] || '', abbreviation: JMD_ABBR[code] || 'jmd.' };
  }
  if (head === 'ETW' || name === 'ETW') {
    return { tokenType: 'slot', id, kind: 'ETW', caseValue: CODE_TO_CASE[code] || '', abbreviation: 'etw.' };
  }
  const caseValue = CODE_TO_CASE[code] || '';
  const abbreviation = code === 'AKKDAT' ? 'jmdn./jmdm.' : `${JMD_ABBR[code] || 'jmd.'}/etw.`;
  return { tokenType: 'slot', id, kind: null, caseValue, abbreviation };
};

/** Split a template into [{ type: 'text', text } | { type: 'token', ...describeToken }]. */
export const parseHeadwordTemplate = (template) => {
  const segments = [];
  if (typeof template !== 'string' || !template) return segments;
  let pos = 0;
  TOKEN_RE.lastIndex = 0;
  let m;
  while ((m = TOKEN_RE.exec(template)) !== null) {
    if (m.index > pos) segments.push({ type: 'text', text: template.slice(pos, m.index) });
    segments.push({ type: 'token', ...describeToken(m[1], m[2] || null) });
    pos = m.index + m[0].length;
  }
  if (pos < template.length) segments.push({ type: 'text', text: template.slice(pos) });
  return segments;
};

/**
 * Split a template into the part inside the phrase (up to the last plain
 * word) and the trailing tokens. Whitespace-only text is dropped from the
 * trailing part; inline text keeps its spacing trimmed per word group.
 */
export const splitHeadwordTemplate = (template) => {
  const segments = parseHeadwordTemplate(template).filter(
    (s) => !(s.type === 'text' && !s.text.trim())
  );
  let lastText = -1;
  segments.forEach((s, i) => {
    if (s.type === 'text') lastText = i;
  });
  return {
    inline: segments.slice(0, lastText + 1).map((s) => (s.type === 'text' ? { ...s, text: s.text.trim() } : s)),
    trailing: segments.slice(lastText + 1),
  };
};

/** Plain display text of segments, with slot abbreviations ("jmdm. aufs Wort glauben"). */
export const segmentsToText = (segments) =>
  (segments || [])
    .map((s) => {
      if (s.type === 'text') return s.text;
      if (s.tokenType === 'slot') return s.abbreviation;
      if (s.tokenType === 'preposition') return s.preposition;
      if (s.tokenType === 'reflexive') return s.caseValue === 'dativ' ? 'sich (+D)' : 'sich';
      return '';
    })
    .filter(Boolean)
    .join(' ');

/** The valency entry a token links to (by id), or null. */
export const findValencyEntry = (grammar, id) => {
  if (!id) return null;
  const valency = grammar?.valency || {};
  const all = [
    ...(Array.isArray(valency.objects) ? valency.objects : []),
    ...(Array.isArray(valency.prepositional_objects) ? valency.prepositional_objects : []),
  ];
  return all.find((e) => e && typeof e === 'object' && e.id === id) || null;
};

const PRONOUN_RE = /^(jemand(en|em|es)?|etwas|jmd[nms]?\.?|jd[nms]\.?|etw\.?)$/i;

/** Example text worth showing next to a slot (not just "etwas"/"jemanden"). */
export const slotExample = (grammar, id) => {
  const entry = findValencyEntry(grammar, id);
  const example = (entry && (entry.example || entry.object_example)) || '';
  return example && !PRONOUN_RE.test(String(example).trim()) ? example : '';
};
